// components/features/clients/ClientDetailsModal.tsx

import { User, Mail, Phone, Calendar, FileText, PhoneCall, Ticket } from "lucide-react";
import { Client } from "../../../types/features/Client";
import BaseModal from "../../ui/Modal";

interface ClientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client | null;
  recentCalls?: any[];
  recentTickets?: any[];
  onNewTicket?: (clientId: string) => void;
  onCall?: (phone: string) => void;
}

export default function ClientDetailsModal({
  isOpen,
  onClose,
  client,
  recentCalls = [],
  recentTickets = [],
  onNewTicket,
  onCall,
}: ClientDetailsModalProps) {
  if (!client) return null;

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatDateTime = (date: string) => {
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} title={`${client.firstname} ${client.lastname}`} size="full">
      <div className="space-y-6">
        {/* Informations personnelles */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Informations personnelles</h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <User size={14} className="text-gray-400" />
              <span className="text-gray-500">Nom complet :</span>
              <span className="text-gray-800">
                {client.firstname} {client.lastname}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail size={14} className="text-gray-400" />
              <span className="text-gray-500">Email :</span>
              <span className="text-gray-800">{client.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Phone size={14} className="text-gray-400" />
              <span className="text-gray-500">Téléphone :</span>
              <span className="text-gray-800">{client.phone}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Calendar size={14} className="text-gray-400" />
              <span className="text-gray-500">Inscrit depuis :</span>
              <span className="text-gray-800">{formatDate(client.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          {onCall && (
            <button
              onClick={() => onCall(client.phone)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <PhoneCall size={16} />
              Appeler
            </button>
          )}
          {onNewTicket && (
            <button
              onClick={() => onNewTicket(client.id)}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Ticket size={16} />
              Nouveau ticket
            </button>
          )}
        </div>

        {/* Derniers appels */}
        {recentCalls.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Derniers appels</h4>
            <div className="space-y-2">
              {recentCalls.map((call, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0"
                >
                  <div className="flex items-center gap-2">
                    <PhoneCall size={12} className="text-gray-400" />
                    <span className="text-gray-600">{formatDateTime(call.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {Math.floor(call.duration / 60)}:{(call.duration % 60).toString().padStart(2, "0")}
                    </span>
                    {call.tags?.map((tag: string, i: number) => (
                      <span key={i} className="text-xs bg-gray-200 px-1.5 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tickets en cours */}
        {recentTickets.length > 0 && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Tickets en cours</h4>
            <div className="space-y-2">
              {recentTickets.map((ticket, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm border-b border-gray-200 pb-2 last:border-0 last:pb-0"
                >
                  <div className="flex items-center gap-2">
                    <FileText size={12} className="text-gray-400" />
                    <span className="text-gray-800">{ticket.title}</span>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      ticket.status === "en_cours"
                        ? "bg-purple-100 text-purple-700"
                        : ticket.status === "en_attente"
                          ? "bg-yellow-100 text-yellow-700"
                          : ticket.status === "escalade"
                            ? "bg-red-100 text-red-700"
                            : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {ticket.status === "en_cours"
                      ? "En cours"
                      : ticket.status === "en_attente"
                        ? "En attente"
                        : ticket.status === "escalade"
                          ? "Escalade"
                          : "Nouveau"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </BaseModal>
  );
}
